import React, { useMemo } from "react";
import { Alert, Button, Spinner, Table } from "react-bootstrap";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import toast from "react-hot-toast";
import {
  useDeleteHeadquarter,
  useHeadquarters,
} from "../hooks/useHeadquarters";
import { useAuth } from "../../../app/providers/AuthProvider";

function HeadquartersList({ onEdit, onManageAccess }) {
  const { data: hqs, isLoading, isError } = useHeadquarters();
  const deleteMutation = useDeleteHeadquarter();
  const { user } = useAuth();
  // Solo el administrador de una sede decide quién más tiene acceso a ella.
  const administers = (hq) =>
    hq.administrator != null && String(hq.administrator) === String(user?.id);

  const handleDelete = (hq) => {
    if (!window.confirm(`¿Eliminar la sede "${hq.reference_name || hq.address}"?`)) return;
    deleteMutation.mutate(hq.id, {
      onSuccess: () => toast.success("Sede eliminada"),
      onError: () => toast.error("Error al eliminar la sede"),
    });
  };

  const columns = useMemo(
    () => [
      { accessorKey: "neighborhood", header: "Barrio" },
      { accessorKey: "address", header: "Dirección" },
      { accessorKey: "reference_name", header: "Referencia" },
      { accessorKey: "phone", header: "Teléfono" },
      {
        id: "administrator",
        header: "Administrador",
        cell: ({ row }) => row.original.administrator_display ?? "—",
      },
      {
        id: "actions",
        header: "Acciones",
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button
              size="sm"
              variant="outline-primary"
              style={{ minHeight: 44 }}
              onClick={() => onEdit(row.original)}
            >
              Editar
            </Button>
            {administers(row.original) && (
              <Button
                size="sm"
                variant="outline-secondary"
                style={{ minHeight: 44 }}
                onClick={() => onManageAccess(row.original)}
              >
                Acceso
              </Button>
            )}
            <Button
              size="sm"
              variant="outline-danger"
              style={{ minHeight: 44 }}
              onClick={() => handleDelete(row.original)}
              disabled={deleteMutation.isPending}
            >
              Eliminar
            </Button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deleteMutation.isPending, user?.id]
  );

  const table = useReactTable({
    data: hqs ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) return <Spinner animation="border" role="status" aria-label="Cargando sedes" />;
  if (isError) return <Alert variant="danger">Error al cargar las sedes.</Alert>;

  const rows = table.getRowModel().rows;

  return (
    <>
      {/* TASK-18: mostrar alerta cuando max_headquarters se alcance */}

      {/* Desktop: tabla con scroll horizontal */}
      <div className="table-responsive d-none d-sm-block">
        <Table striped bordered hover className="mb-0">
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((h) => (
                  <th key={h.id}>
                    {flexRender(h.column.columnDef.header, h.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted py-3">
                  Sin sedes registradas
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Mobile: tarjetas apiladas */}
      <div className="d-sm-none">
        {(hqs ?? []).length === 0 && (
          <p className="text-muted text-center py-3">Sin sedes registradas</p>
        )}
        {(hqs ?? []).map((hq) => (
          <div key={hq.id} className="card mb-3">
            <div className="card-body">
              <h6 className="card-title mb-2">{hq.reference_name || hq.address}</h6>
              <p className="mb-1"><strong>Barrio:</strong> {hq.neighborhood}</p>
              <p className="mb-1"><strong>Dirección:</strong> {hq.address}</p>
              <p className="mb-1"><strong>Teléfono:</strong> {hq.phone}</p>
              <p className="mb-3">
                <strong>Administrador:</strong> {hq.administrator_display ?? "—"}
              </p>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-primary"
                  style={{ minHeight: 44, flex: 1 }}
                  onClick={() => onEdit(hq)}
                >
                  Editar
                </Button>
                {administers(hq) && (
                  <Button
                    variant="outline-secondary"
                    style={{ minHeight: 44, flex: 1 }}
                    onClick={() => onManageAccess(hq)}
                  >
                    Acceso
                  </Button>
                )}
                <Button
                  variant="outline-danger"
                  style={{ minHeight: 44, flex: 1 }}
                  onClick={() => handleDelete(hq)}
                  disabled={deleteMutation.isPending}
                >
                  Eliminar
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

export { HeadquartersList };
